import {
  Button,
  ButtonVariants,
  IError,
  Modal,
  ModalVariants,
  TextField,
  useToast,
} from '@comptia-sso/core';
import { ChangeEvent, ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';

// Services.
import { sendLmsVerificationEmail, verifyLmsEmail } from 'services';

// Types.
import { IChangeEmailRequest } from 'types';

// Styles.
import styles from './EmailVerificationPrompt.module.scss';

interface IEmailVerificationPromptProps {
  dismissable?: boolean;
  onClose?: () => void;
  onSuccess: () => void;
  open: boolean;
  profileId: string;
}

type Step = 'send' | 'verify';

export const EmailVerificationPrompt = ({
  dismissable = true,
  onClose,
  onSuccess,
  open,
  profileId,
}: IEmailVerificationPromptProps): ReactElement => {
  const { t } = useTranslation();
  const [popToast] = useToast();
  const [step, setStep] = useState<Step>('send');
  const [code, setCode] = useState<string>('');
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const handleSend = async () => {
    setIsLoading(true);
    try {
      await sendLmsVerificationEmail(profileId);
      setStep('verify');
    } catch (error) {
      popToast((error as IError)?.message || t('Toast.Error.Default'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleVerify = async () => {
    setIsLoading(true);
    try {
      const values: IChangeEmailRequest = { verificationCode: code };
      await verifyLmsEmail(profileId, values);
      popToast(t('EmailVerification.Success'));
      onSuccess();
    } catch (error) {
      popToast((error as IError)?.message || t('Toast.Error.Default'));
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeChange = (e: ChangeEvent<HTMLInputElement>) => {
    setCode(e.target.value);
  };

  return (
    <Modal
      disableClose={!dismissable}
      onClose={onClose}
      open={open}
      title={t('EmailVerification.Title')}
      variant={ModalVariants.Small}
    >
      {step === 'send' && (
        <div className={styles.content}>
          <p>{t('EmailVerification.Description')}</p>
          <Button
            className={styles.fullWidth}
            disabled={isLoading}
            onClick={handleSend}
          >
            {t('EmailVerification.SendCode')}
          </Button>
        </div>
      )}
      {step === 'verify' && (
        <div className={styles.content}>
          <p>{t('EmailVerification.CodeSent')}</p>
          <TextField
            label={t('EmailVerification.CodeLabel')}
            onChange={handleCodeChange}
            value={code}
          />
          <div className={styles.actions}>
            <Button
              className={styles.fullWidth}
              disabled={isLoading || !code}
              onClick={handleVerify}
            >
              {t('EmailVerification.VerifyButton')}
            </Button>
            <Button
              className={styles.fullWidth}
              disabled={isLoading}
              onClick={handleSend}
              variant={ButtonVariants.Text}
            >
              {t('EmailVerification.ResendCode')}
            </Button>
          </div>
        </div>
      )}
    </Modal>
  );
};
